import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { g } from "analytics";
import { clearToken, getCrashedFile, removeCrashedFile, retrieveToken, saveCrashFile } from "api/auth/storage";
import { disposeMarkSymbol } from "apparatus/draw";
import { commandProcessor } from "command/command";
import { IconButton } from "components/button";
import { Selected } from "editing/selection";
import { openDiagram } from "file/api";
import { DiagramDatas } from "file/loading";
import { setUpKeyboard } from "keyboard";
import { EditorContainer } from "layout/editor/editor_container";
import { LoginModalProvider, SharedModalsProvider } from "layout/modals/shared_modals";
import { TabbedSidebar } from "layout/sidebar/tabbed_sidebar";
import { ScreenTooSmallWarning } from "layout/top_bar/extras";
import { TopBar } from "layout/top_bar/top_bar";
import { clog } from "log";
import { PropertyPanelAdapter } from "panel/property_panel_adapter";
import * as React from "react";
import { Component } from "react";
import { connect, shallowEqual } from "react-redux";
import { logIn } from "store/actions";
import store, { extractDebugInfoFromStore } from "store/store";
import { Bcn } from "support/beacon";
import { TierContextProvider } from "tiers/context-provider";
import { callApi } from "../api/api";
import { setGloballyStrokeThicknessTheme } from "../apparatus/common";
import { FileContext } from "../file/context";
import { shouldShowWelcome, WelcomeModal2 } from "./modals/welcome";
const DELAY_SUPPORT_SERVICE_LOAD_MS = 500;
const SENTRY_REPORT_DIALOG_OPTIONS = {
    title: "Oops! Chemix has unexpectedly crashed.",
    subtitle: "We're sorry about this, our team has been notified. Please help us by providing additional information.",
    labelComments: "Please describe in as much detail the last steps you took before the error happened:",
};
/** Top-level App component. */
export class App extends Component {
    constructor(props) {
        super(props);
        /** Reference to the editor container. Needed for image export. */
        this.editorContainerRef = React.createRef();
        this.queryClient = new QueryClient();
        this.render = () => {
            if (this.state.hasError) {
                return React.createElement("div", { key: "error", className: "error-bar" },
                    React.createElement("i", { className: "fa fa-dizzy fa-2x mr-2" }),
                    "Oops! Unfortunately Chemix has crashed.",
                    React.createElement("div", { className: "d-inline-block" },
                        React.createElement("button", { type: "button", className: "btn btn-outline-light mx-1", onClick: () => { g('CrashBar', 'SentryUserReport'); Sentry.showReportDialog(Object.assign({ eventId: this.state.errorId }, SENTRY_REPORT_DIALOG_OPTIONS)); } }, "Report crash"),
                        React.createElement("button", { type: "button", className: "btn btn-outline-light mx-1", onClick: () => { g('CrashBar', 'OpenChat'); Bcn.ask(); } }, "Chat to us"),
                        React.createElement("button", { type: "button", className: "btn btn-outline-light mx-1", onClick: () => { g('CrashBar', 'Reload'); window.location.reload(); } }, "Reload Chemix")));
            }
            return (React.createElement(QueryClientProvider, { client: this.queryClient },
                React.createElement(FileContext.Provider, { value: this },
                    React.createElement(LoginModalProvider, { auth: this.props.auth },
                        React.createElement(TierContextProvider, { auth: this.props.auth, tier: this.props.tier },
                            React.createElement(SharedModalsProvider, { auth: this.props.auth },
                                React.createElement(TopBar, { key: "topBar", tool: this.props.tool, auth: this.props.auth, file: this, cloudDiagramTitle: this.props.cloudDiagramTitle, isMaximized: this.state.hideApparatusPanel && this.state.collapsePropertyPanel, setMaximized: (state) => this.setState({ hideApparatusPanel: state, collapsePropertyPanel: state }) }),
                                React.createElement(EditorContainer, Object.assign({ key: "editorContainer", ref: this.editorContainerRef }, this.props, { onPaperReady: () => this.onPaperReady() })),
                                this.state.isPaperReady && React.createElement(PropertyPanelAdapter, { collapsed: this.state.collapsePropertyPanel, onToggleCollapsed: (state) => this.setState({ collapsePropertyPanel: state }), key: "PropertyPanelAdapter", selected: this.props.selected, app: this.editorContainerRef.current, theme: this.props.theme }),
                                !this.state.hideApparatusPanel
                                    && React.createElement(TabbedSidebar, { key: "Sidebar", onHidePanel: () => this.setState({ hideApparatusPanel: true }) }),
                                this.state.hideApparatusPanel
                                    && React.createElement(IconButton, { icon: "plus", extraClass: "show-sidebar-button", buttonLabel: "Show sidebar", onClick: () => this.setState({ hideApparatusPanel: false }) }),
                                React.createElement(ScreenTooSmallWarning, null),
                                React.createElement(WelcomeModal2, { isOpen: this.state.showWelcomeScreen, setIsOpen: (open) => this.setState({ showWelcomeScreen: open }) })))))));
        };
        this.state = { hasError: false, showWelcomeScreen: false, isPaperReady: false, hideApparatusPanel: false, collapsePropertyPanel: false };
    }
    onPaperReady() {
        this.setState({ isPaperReady: true });
    }
    componentDidMount() {
        // Set up keyboard short cuts.
        setUpKeyboard({
            toggleMaximized: () => {
                const isMaximized = this.state.hideApparatusPanel && this.state.collapsePropertyPanel;
                this.setState({ hideApparatusPanel: !isMaximized, collapsePropertyPanel: !isMaximized });
            }
        });
        // Hide the loading message.
        $("#loading-message").addClass("loaded");
        // Welcome screen
        this.setState({ showWelcomeScreen: shouldShowWelcome() });
        // Load support service (Beacon).
        Bcn.init();
        setTimeout(() => Bcn.load(), DELAY_SUPPORT_SERVICE_LOAD_MS);
        // Check for crashed file.
        const maybeCrashedFile = getCrashedFile();
        if (maybeCrashedFile) {
            if (confirm("It looks like your previous session crashed. Would you like to recover the last diagram?")) {
                openDiagram({
                    id: "",
                    title: "",
                    thumbnail: "",
                    modified: new Date(),
                    created: new Date(),
                    owner: "",
                    sharedWith: [],
                    rawData: maybeCrashedFile.documentSerialized,
                }, /* isTemplate */ false);
                Sentry.addBreadcrumb({ category: "app", level: "info", message: "Recover crashed file" });
                g("Crash", "Recover crashed file");
            }
            // Remove regardless of the user choosing to reload it or not.
            removeCrashedFile();
        }
        // If there is a stored token, trigger a fetch to see
        // if it is still valid. If it is, set up a session
        // from it.
        this.maybeRestoreAuthSession();
        // Animate in the app. This is done by a CSS transition,
        // by adding the "loaded" class.
        $("#app").addClass("loaded");
        // Load Twitter widgets.
        const twitterScript = document.createElement("script");
        twitterScript.src = "https://platform.twitter.com/widgets.js";
        twitterScript.async = true;
        document.body.appendChild(twitterScript);
    }
    componentDidUpdate(prevProps) {
        var forceRerenderComponents = false;
        // Update theme.
        if (prevProps.theme.strokeThickness != this.props.theme.strokeThickness) {
            setGloballyStrokeThicknessTheme(this.props.theme.strokeThickness);
            // Force re-render every component.
            disposeMarkSymbol();
            forceRerenderComponents = true;
        }
        // Check ChemMark props.
        if (!shallowEqual(prevProps.chemMark, this.props.chemMark)) {
            forceRerenderComponents = true;
        }
        // ForceRerenderComponents
        if (forceRerenderComponents) {
            this.forceRerenderComponents();
        }
    }
    forceRerenderComponents() {
        clog("Force rerender components");
        for (const item of this.props.items) {
            const component = this.editorContainerRef.current.getApparatusComponent(item.id);
            component === null || component === void 0 ? void 0 : component.forceCompleteRerender();
            component === null || component === void 0 ? void 0 : component.forceUpdate();
        }
        for (const arrow of this.props.arrows) {
            const component = this.editorContainerRef.current.getObjectComponent(arrow.id);
            component.forceUpdate();
        }
    }
    exportImages(options) {
        // This is bit hacky, we expose the export image methods on
        // the editor container so that we can call into its
        // child editor canvas.
        return this.editorContainerRef.current.exportImages(options);
    }
    exportThumbnail() {
        // This is also hacky.
        return this.editorContainerRef.current.exportThumbnail();
    }
    maybeRestoreAuthSession() {
        let token = retrieveToken();
        if (!token) {
            clog("No token found to restore session from.");
            return;
        }
        callApi("reauth", "GET", token, undefined, {
            success: response => {
                if (response.type == "success") {
                    store.dispatch(logIn({
                        provider: response.provider,
                        jwtToken: response.jwt,
                        user: response.user,
                        // This should presumably be false.
                        firstTimeLogin: response.firstTimeLogin,
                        tier: response.tier,
                    }));
                }
                else {
                    clearToken();
                    clog("Auth not restored from stored token.");
                }
            },
            generic: () => {
                // On error, clear the token.
                clearToken();
            }
        });
    }
    // Error handling.
    static getDerivedStateFromError() {
        return { hasError: true, showWelcomeScreen: false, isPaperReady: true, collapsePropertyPanel: false, hideApparatusPanel: false };
    }
    componentDidCatch(error, errorInfo) {
        // Report to Sentry: https://docs.sentry.io/platforms/javascript/react/
        Sentry.withScope((scope) => {
            scope.setExtra("componentStack", errorInfo.componentStack);
            scope.setExtra("store", extractDebugInfoFromStore());
            scope.setTag("fatalcrash", "yes");
            Sentry.addBreadcrumb({ category: "app", level: "fatal", message: "Error captured at error boundary" });
            let eventId = Sentry.captureException(error);
            // Only set the errorId once.
            if (!this.state.errorId) {
                g("Crash");
                this.setState({ errorId: eventId });
                Sentry.showReportDialog(Object.assign({ eventId: eventId }, SENTRY_REPORT_DIALOG_OPTIONS));
                // Save a crash file.
                // Revert last command and save state to local storage. Wait a bit because the crash
                // triggered by __tonngokhong__ doesn't seem to register immediately.
                // This may be called multiple times on error, so only call it once.
                setTimeout(() => {
                    commandProcessor.undo();
                    saveCrashFile(DiagramDatas.getCurrentDocument());
                }, 1000);
            }
        });
    }
}
// React-Redux.
/** Converts the Redux state to App component's props. */
const mapStateToProps = (store, _) => {
    var _a;
    const selectedItems = store.items.filter((item) => item.selected);
    const selectedArrows = store.arrows.filter(arrow => arrow.selected);
    return {
        zoom: store.zoom,
        items: store.items,
        arrows: store.arrows,
        selected: new Selected(selectedItems, selectedArrows),
        tool: store.tool,
        auth: store.auth,
        tier: store.tier,
        cloudDiagramTitle: (_a = store.currentCloudDiagram) === null || _a === void 0 ? void 0 : _a.title,
        theme: store.theme,
        renderArea: store.renderArea,
        // Needed to trigger update on config change.
        chemMark: store.chemMark,
    };
};
/** Container Component for App. */
export const ConnectedApp = connect(mapStateToProps)(App);
